// Pure compute & format helpers for the ERP business modules.
//
// There is NO sample data here any more — all records (inventory, sales,
// purchase, employees, production orders, BOMs, stock movements) are loaded
// from / saved to ThingsBoard via Services/app/erpservice.js. These functions
// operate purely on the arrays the screens hold in state.

// Explode the BOM for every completed production order into stock movements:
// components are consumed (OUT), finished goods are produced (IN).
export function computeProductionMovements(productionOrders = [], boms = []) {
  const moves = [];
  productionOrders
    .filter((o) => o.status === 'Completed')
    .forEach((o) => {
      const bom = boms.find((b) => b.fg === o.fg);
      if (!bom) return;
      (bom.components || []).forEach((c) => {
        moves.push({
          date: o.date, code: c.code, type: 'OUT',
          qty: Math.round(c.qtyPer * o.qty * 100) / 100,
          ref: o.id, source: 'Production',
        });
      });
      moves.push({ date: o.date, code: o.fg, type: 'IN', qty: o.qty, ref: o.id, source: 'Production' });
    });
  return moves;
}

// Manual movements (goods receipts, sales dispatch) + production-derived ones,
// newest first.
export function computeAllMovements(stockMovements = [], productionOrders = [], boms = []) {
  return [...stockMovements, ...computeProductionMovements(productionOrders, boms)]
    .sort((a, b) => String(b.date).localeCompare(String(a.date)));
}

// Live on-hand per item code = opening balance +/- every movement.
export function computeStockLevels(inventory = [], allMovements = []) {
  const level = {};
  inventory.forEach((i) => { level[i.code] = Number(i.opening) || 0; });
  allMovements.forEach((m) => {
    if (!(m.code in level)) level[m.code] = 0;
    level[m.code] += m.type === 'IN' ? m.qty : -m.qty;
  });
  return level;
}

// MRP-lite: component requirement vs available stock for one production order.
export function computeMaterialPlan(order, boms = [], inventory = [], levels = {}) {
  if (!order) return [];
  const bom = boms.find((b) => b.fg === order.fg);
  if (!bom) return [];
  return (bom.components || []).map((c) => {
    const required = Math.round(c.qtyPer * order.qty * 100) / 100;
    const available = levels[c.code] ?? 0;
    const item = inventory.find((i) => i.code === c.code);
    return {
      code: c.code,
      name: item ? item.name : c.code,
      uom: c.uom,
      required,
      available,
      shortfall: Math.max(0, Math.round((required - available) * 100) / 100),
    };
  });
}

export function formatNumber(n, digits = 0) {
  if (n === undefined || n === null || Number.isNaN(n)) return '—';
  return n.toLocaleString('en-IN', { maximumFractionDigits: digits, minimumFractionDigits: digits });
}

export function formatCurrency(n) {
  if (n === undefined || n === null) return '—';
  return n.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 });
}
